import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, ShoppingBag, Check, Star, Heart, Sparkles, Sun, Snowflake, Leaf, Pause, Play } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import { useSocialProofEnabled } from '@/hooks/useSocialProofEnabled';

/**
 * FloatingTrending — left-side sidebar that cycles through promotional items
 * in groups of 3 (window size = VISIBLE_COUNT). To change items, edit the
 * `trendingItems` array below. `isHot` items get a heart badge + pink accent.
 * Rotation auto-advances every ROTATE_MS and pauses when hovered.
 *
 * Palette is locked to a unified "Clinic Champagne" theme (cream marble header
 * + brass/champagne accents on a deep stone body) to mirror the clinic's
 * elegant in-room aesthetic. The seasonal icon (Sparkles / Sun / Leaf /
 * Snowflake) still rotates with the month for thematic charm.
 */

const VISIBLE_COUNT = 3;
const CARD_HEIGHT = 132; // px — tight, denser layout (no wasted vertical slack)
const SECONDS_PER_CARD = 7; // controls overall wheel speed

// Unified "Clinic Champagne" palette — cream marble + warm brass on stone.
// Pulled from the actual Karine Lauzon RN Clinic interior (champagne gold
// trim, ivory walls, soft taupe upholstery).
const CLINIC_PALETTE = {
  headerGradient: 'from-stone-100 via-amber-50 to-stone-100',
  headerHover: 'hover:from-stone-50 hover:via-amber-100 hover:to-stone-50',
  headerText: 'text-stone-800',
  border: 'border-amber-300/70',
  glow: 'shadow-[0_8px_30px_rgba(212,175,116,0.28)]',
  pulseRgb: '212, 175, 116',
  ringClass: 'ring-amber-300/40',
  bgHighlight: 'bg-amber-500/[0.07]',
  priceText: 'text-amber-300',
  starText: 'text-amber-400',
  starFill: 'fill-amber-400',
  nameHighlight: 'text-amber-200',
  nameHover: 'group-hover:text-amber-200',
  btnBg: 'bg-gradient-to-r from-amber-100 via-yellow-50 to-amber-100 hover:from-amber-50 hover:via-white hover:to-amber-50',
  btnText: 'text-stone-900',
  dotActive: 'bg-amber-300',
};

// Seasonal icon stays — purely decorative thematic charm.
const SEASON_ICONS = {
  spring: Sparkles,
  summer: Sun,
  fall: Leaf,
  winter: Snowflake,
};

const seasonForMonth = (m) => {
  // m is 0-11; matches Northern Hemisphere meteorological seasons.
  if (m >= 2 && m <= 4) return 'spring';   // Mar–May
  if (m >= 5 && m <= 7) return 'summer';   // Jun–Aug
  if (m >= 8 && m <= 10) return 'fall';    // Sep–Nov
  return 'winter';                          // Dec, Jan, Feb
};

// Easy to edit — just add/remove/reorder items here.
const trendingItems = [
  // ---- Summer Dermal Filler Event 2026 ----
  {
    id: 'summer-filler-1',
    name: 'Summer Filler Event — One Syringe + Free Lip Flip',
    price: '$499',
    tag: '☀️',
    badge: 'SUMMER',
    color: 'bg-gradient-to-br from-amber-400 to-yellow-500',
    accentRing: 'ring-amber-400/50',
    href: '/promotions',
    cartType: 'filler',
    numPrice: 499,
    isHot: true,
  },
  // ---- Evergreen Trending ----
  {
    id: 'trend-full-upper',
    name: 'Full Upper Face Botox',
    price: '$599',
    tag: '#1',
    color: 'bg-violet-500',
    href: '/botox-ottawa',
    cartType: 'botox',
    numPrice: 599,
  },
  {
    id: 'trend-nefertiti',
    name: 'Nefertiti + Skin Booster',
    price: '$899',
    tag: '#2',
    color: 'bg-amber-500',
    href: '/skin-boosters',
    cartType: 'combo',
    numPrice: 899,
  },
  {
    id: 'trend-kiss-lips',
    name: 'Kiss Me Lips + Lip Flip',
    price: '$499',
    tag: '#3',
    color: 'bg-rose-500',
    href: '/dermal-fillers',
    cartType: 'filler',
    numPrice: 499,
  },
];

const FloatingTrending = () => {
  const socialProofEnabled = useSocialProofEnabled();
  const [collapsed, setCollapsed] = useState(false);
  const [hoverPaused, setHoverPaused] = useState(false);
  const [manualPaused, setManualPaused] = useState(false);
  const [items, setItems] = useState(trendingItems);  // fallback to static
  const [featuredTag, setFeaturedTag] = useState('');
  const [featuredEmoji, setFeaturedEmoji] = useState('');
  const paused = hoverPaused || manualPaused;
  const { addToCart, isInCart } = useCart();

  // Unified clinic palette — recomputed once per mount, just to keep the
  // seasonal Icon swap. Colors are locked to champagne/brass for brand cohesion.
  const palette = useMemo(() => CLINIC_PALETTE, []);
  const SeasonIcon = useMemo(() => SEASON_ICONS[seasonForMonth(new Date().getMonth())], []);

  // Fetch admin-managed trending list + featured chip from backend
  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trending`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (cancelled || !data) return;
        const remote = data.items || [];
        if (remote.length > 0) setItems(remote);
        setFeaturedTag(data.featured_tag || '');
        setFeaturedEmoji(data.featured_emoji || '');
      })
      .catch(() => { /* silent — keep static fallback */ });
    return () => { cancelled = true; };
  }, []);

  // Hide the floating widget entirely when the operator has the
  // "Social Proof Widget" automation OFF (default). Placed AFTER all hooks
  // to comply with React's rules-of-hooks (hooks must always run in the
  // same order regardless of conditional rendering).
  if (!socialProofEnabled) return null;

  const handleAdd = (item) => {
    if (isInCart(item.id)) return;
    addToCart({
      id: item.id,
      name: item.name,
      category: item.badge || 'Trending This Month',
      price: item.price,
      type: item.cartType,
      duration: 45,
    });
    toast.success(`${item.name} added!`, { duration: 2000 });
  };

  // Render a single card — extracted so we can render it twice for the seamless wheel loop
  const renderCard = (item, keyPrefix) => {
    const inCart = isInCart(item.id);
    return (
      <div
        key={`${keyPrefix}-${item.id}`}
        className="px-2.5 py-1.5 border-b border-stone-700 box-border"
        style={{ height: `${CARD_HEIGHT}px` }}
        data-testid={`trending-${item.id}`}
      >
        {item.badge && (
          <div className="mb-1 inline-flex items-center gap-1 px-1.5 py-[1px] rounded-full text-[8px] font-bold tracking-wider bg-gradient-to-r from-pink-500 to-rose-500 text-white">
            <Heart className="w-2 h-2 fill-white" />
            {item.badge}
          </div>
        )}
        <Link to={item.href} className="flex items-start gap-2 group">
          <span
            className={`${item.color} text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md flex-shrink-0 shadow-md`}
          >
            {item.tag}
          </span>
          <div className="flex-1 min-w-0">
            <p className={`text-[11px] font-semibold leading-tight text-white ${palette.nameHover} transition-colors line-clamp-2`}>
              {item.name}
            </p>
            <p className={`${palette.priceText} text-sm font-bold leading-none mt-0.5`}>
              {item.price}
              <span className="text-stone-500 text-[10px] font-normal ml-1">+tax</span>
            </p>
            <p className="text-stone-500 text-[9px] mt-0.5 leading-tight">
              <span className="bg-black text-[#b2fce4] text-[7px] font-bold px-1 py-0.5 rounded mr-1">afterpay</span>
              4 x ${(item.numPrice / 4).toFixed(2)}
            </p>
            <p className={`${palette.starText} text-[9px] flex items-center gap-0.5 leading-tight`}>
              <Star className={`w-2 h-2 ${palette.starFill} ${palette.starText}`} />
              Earn {item.numPrice} pts
            </p>
          </div>
        </Link>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleAdd(item);
          }}
          className={`w-full mt-1 text-[10.5px] font-semibold py-1 rounded-md transition-all flex items-center justify-center gap-1 border ${
            inCart ? 'bg-emerald-700/90 text-white border-emerald-600' : `${palette.btnBg} ${palette.btnText} border-amber-200/60 shadow-sm`
          }`}
          data-testid={`trending-add-${item.id}`}
        >
          {inCart ? (
            <>
              <Check className="w-3 h-3" /> Added
            </>
          ) : (
            <>
              <ShoppingBag className="w-3 h-3" /> Book Now · Add to Cart
            </>
          )}
        </button>
      </div>
    );
  };

  const shouldLoop = items.length > VISIBLE_COUNT;
  const wheelDuration = `${items.length * SECONDS_PER_CARD}s`;
  const visibleHeight = CARD_HEIGHT * VISIBLE_COUNT;

  return (
    <div
      className="fixed left-3 sm:left-5 top-[260px] z-[80] hidden md:block kl-scroll-fade"
      data-testid="floating-trending"
      onMouseEnter={() => setHoverPaused(true)}
      onMouseLeave={() => setHoverPaused(false)}
    >
      {/* Toggle Header — cream marble bar with brass accent */}
      <div className={`flex items-center bg-gradient-to-r ${palette.headerGradient} ${palette.headerText} px-3 py-2 rounded-t-xl text-[10px] font-bold uppercase tracking-wider border-2 ${palette.border} border-b-0 transition-all w-full shadow-lg animate-pulse-subtle`}>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center gap-1.5 flex-1 text-left hover:opacity-90"
          data-testid="trending-collapse-toggle"
        >
          <SeasonIcon className="w-3.5 h-3.5 animate-flicker" />
          <span>Trending This Month</span>
          {paused && !collapsed && (
            <span className="ml-1 px-1.5 py-0.5 bg-stone-800/15 rounded-full text-[8px] tracking-wide text-stone-700" data-testid="trending-paused-pill">
              {hoverPaused && !manualPaused ? 'reading...' : 'paused'}
            </span>
          )}
        </button>
        {!collapsed && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setManualPaused((v) => !v);
            }}
            className="p-1 rounded-full hover:bg-stone-800/10 transition-colors"
            aria-label={manualPaused ? 'Resume rotation' : 'Pause rotation'}
            title={manualPaused ? 'Resume rotation' : 'Pause rotation'}
            data-testid="trending-play-pause"
          >
            {manualPaused
              ? <Play className="w-3 h-3" fill="currentColor" />
              : <Pause className="w-3 h-3" fill="currentColor" />}
          </button>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 rounded-full hover:bg-stone-800/10 transition-colors ml-0.5"
          aria-label={collapsed ? 'Expand' : 'Collapse'}
          data-testid="trending-chevron"
        >
          {collapsed ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Cards window — continuous wheel-style vertical rotation */}
      {!collapsed && (
        <div className={`bg-stone-900 border-2 ${palette.border} border-t-0 rounded-b-xl overflow-hidden ${palette.glow} w-[210px]`}>
          {/* Optional Featured Tag of the Month chip — admin-controlled */}
          {(featuredTag || featuredEmoji) && (
            <div className="px-2 pt-2 pb-1 flex items-center justify-center" data-testid="trending-featured-chip">
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-amber-100 via-yellow-50 to-amber-100 text-stone-900 border border-amber-300 shadow-sm max-w-full truncate">
                {featuredEmoji && <span aria-hidden="true">{featuredEmoji}</span>}
                {featuredTag && <span>{featuredTag}</span>}
              </span>
            </div>
          )}

          {/* Wheel viewport — fixed height window so we can scroll the strip behind it */}
          <div
            className="relative overflow-hidden"
            style={{ height: shouldLoop ? `${visibleHeight}px` : 'auto' }}
            data-testid="trending-wheel-viewport"
          >
            {/* Soft fade masks at top + bottom for the "wheel" feel */}
            {shouldLoop && (
              <>
                <div className="pointer-events-none absolute top-0 left-0 right-0 h-6 z-10 bg-gradient-to-b from-stone-900 to-transparent" />
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 z-10 bg-gradient-to-t from-stone-900 to-transparent" />
              </>
            )}
            <div
              className={shouldLoop ? 'flex flex-col animate-wheel-scroll' : 'flex flex-col'}
              style={shouldLoop ? {
                animationDuration: wheelDuration,
                animationPlayState: paused ? 'paused' : 'running',
              } : undefined}
              data-testid="trending-wheel-strip"
            >
              {items.map((item) => renderCard(item, 'a'))}
              {shouldLoop && items.map((item) => renderCard(item, 'b'))}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { box-shadow: 0 0 0 0 rgba(${palette.pulseRgb}, 0.4); }
          50% { box-shadow: 0 0 14px 5px rgba(${palette.pulseRgb}, 0.25); }
        }
        .animate-pulse-subtle {
          animation: pulse-subtle 3s ease-in-out infinite;
        }
        @keyframes flicker {
          0%, 100% { opacity: 1; transform: rotate(0deg) scale(1); }
          50% { opacity: 0.7; transform: rotate(-8deg) scale(1.15); }
        }
        .animate-flicker {
          animation: flicker 2s ease-in-out infinite;
        }
        /* Continuous "wheel" rotation — items flow upward as a single belt.
           Strip is rendered twice; translating from 0 to -50% lands the
           duplicate set exactly where the original was, looping seamlessly. */
        @keyframes wheel-scroll {
          from { transform: translate3d(0, 0, 0); }
          to   { transform: translate3d(0, -50%, 0); }
        }
        .animate-wheel-scroll {
          animation-name: wheel-scroll;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
          will-change: transform;
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-wheel-scroll { animation: none !important; }
        }
      `}</style>
    </div>
  );
};

export default FloatingTrending;
